import { useCallback, useEffect, useState } from "react";
import { ITEM_STATS, ItemName, ItemPool, Region, regionPools } from "../item/item-types";
import './filter.css';
import Border, { BorderType } from "./border";

const initialFilter: ItemPool[] = [
    ItemPool.Unobtainable,
    ItemPool.Woodland_1,
    ItemPool.Woodland_2,
    ItemPool.Woodland_3,
    ItemPool.Swampland_1,
    ItemPool.Swampland_2,
    ItemPool.Swampland_3,
    ItemPool.Halloween,
]

type FilterProps = {
    items: ItemName[];
    setFilteredItems: React.Dispatch<React.SetStateAction<ItemName[]>>;
};

function getFilterIcon(pool: ItemPool, active: boolean) {
    let base: string = "";
    if (pool.startsWith("woodland_")) {
        base = "woodland";
    } else if (pool.startsWith("swampland_")) {
        base = "swampland";
    } else if (pool.startsWith("halloween")) {
        base = "halloween";
    } else if (pool.startsWith("easter")) {
        base = "easter";
    }
    return active
        ? `/resources/menu/filter/filter_icon_${pool}.png`
        : `/resources/menu/filter/filter_icon_${base}_off.png`;
}

const Filter: React.FC<FilterProps> = ({ items, setFilteredItems }) => {
    const [selectedPools, setSelectedPools] = useState<ItemPool[]>(initialFilter);

    const togglePool = useCallback((pool: ItemPool) => {
        setSelectedPools(prev =>
            prev.includes(pool)
                ? prev.filter(p => p !== pool)
                : [...prev, pool]
        );
    }, []);

    const handleRegionClick = useCallback((region: Region) => {
        setSelectedPools(prev => {
            const pools = regionPools[region];
            const anyEnabled = pools.some(pool => prev.includes(pool));
            if (anyEnabled) {
                return prev.filter(pool => !pools.includes(pool));
            } else {
                return [...prev, ...pools.filter(pool => !prev.includes(pool))];
            }
        });
    }, []);

    useEffect(() => {
        setFilteredItems(items.filter(item => {
            const stats = ITEM_STATS[item];
            return !stats.itemPool || selectedPools.includes(stats.itemPool);
        }));
    }, [items, setFilteredItems, selectedPools]);

    return (
        <div className="item-filter">
            <div className="item-filter-content">
                <span className='item-filter-title'>Filters:</span>
                <div className="item-filter-buttons">
                    {Object.entries(regionPools).map(([region, pools]) => {
                        const active = selectedPools.some(pool => pools.includes(pool))
                        return (
                            <div key={region} className="filter-region-group">
                                <span
                                    className={`filter-region-title${active ? " active" : ""}`}
                                    style={{ cursor: "pointer", userSelect: "none" }}
                                    onClick={() => handleRegionClick(region as Region)}
                                >
                                    <Border type={BorderType.Tag} active={active}>
                                        <span className='filter-region-title-text'>
                                            {region.charAt(0).toUpperCase() + region.slice(1)}
                                        </span>
                                    </Border>
                                </span>
                                <div className="filter-region-buttons">
                                    {pools.map(pool => (
                                        <button
                                            key={pool}
                                            onClick={() => togglePool(pool)}
                                            className={selectedPools.includes(pool) ? "active" : ""}
                                        >
                                            <img
                                                src={getFilterIcon(pool, selectedPools.includes(pool))}
                                                alt={pool}
                                            />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default Filter;